import React, { RefObject } from 'react';
import { useActor, useSelector } from '@xstate/react';
import { reach } from 'yup';

import { PaymentProfile, CreditCardFields } from '@chargify/payments-sdk';
import { AddressFields } from '@chargify/address';

import {
  PaymentProfileInterpreter,
  PaymentProfileFromType,
  PaymentProfileFieldError,
  PaymentMethod,
  paymentProfileSelectors,
  paymentGatewaySelectors,
} from '../../machines';

import { useServices } from '../../hooks';

export type CreatePaymentProfileContainerProps = {
  /**
   * HTML form element identifier
   */
  formElementId: string;
  /**
   * Payment profile machine reference
   */
  paymentProfileRef: PaymentProfileInterpreter;
  /**
   * Payment method type
   */
  paymentMethodType: PaymentMethod;
  /**
   * Element to which the dropdwon should be closed on outside click
   */
  targetElementRef?: RefObject<HTMLElement>;
};

const CreatePaymentProfileContainer = ({
  formElementId,
  paymentProfileRef,
  targetElementRef,
}: CreatePaymentProfileContainerProps) => {
  const [, sendToMachine] = useActor(paymentProfileRef);
  const { paymentGatewayService } = useServices();

  const useThreeDSecure = useSelector(
    paymentGatewayService,
    paymentGatewaySelectors.getThreeDSecure
  );

  const gatewayConfig = useSelector(
    paymentGatewayService,
    paymentGatewaySelectors.getGatewayConfig
  );

  const requiredFields = useSelector(
    paymentProfileRef,
    paymentProfileSelectors.getRequiredFields
  );

  const { validationSchema, billingAddress, paymentMethod } = useSelector(
    paymentProfileRef,
    paymentProfileSelectors.getPaymentProfile
  );

  const validateField = (
    fieldName: keyof CreditCardFields | keyof AddressFields,
    form: PaymentProfileFromType,
    value: string | number
  ) => {
    const fieldSchema = reach(validationSchema, `${form}.${fieldName}`);
    fieldSchema
      .validate(value)
      .then(() => {
        sendToMachine({
          type: 'RESET_FIELD_ERROR',
          field: fieldName,
          form,
        });
      })
      .catch((error: { message: PaymentProfileFieldError }) => {
        sendToMachine({
          type: 'SET_FIELD_ERROR',
          error: error.message,
          field: fieldName,
          form,
        });
      });
  };

  return (
    <PaymentProfile
      formElementId={formElementId}
      config={gatewayConfig}
      requiredFields={requiredFields}
      useThreeDSecure={useThreeDSecure}
      paymentMethod={paymentMethod}
      billingAddress={billingAddress}
      onValidateField={validateField}
      targetElementRef={targetElementRef}
      onUpdateField={(field, form, value) => {
        sendToMachine({
          type: 'UPDATE_FIELD',
          form,
          field,
          value,
        });
      }}
    />
  );
};

export default CreatePaymentProfileContainer;
